/**
 * Operator schemas — F-007 Phase 1 (IMPL-P1-003).
 *
 * Schemi Zod e tipi DTO usati sia dalla validazione API che dai componenti
 * frontend per il flusso operatore (`/operatore`).
 *
 * Pattern (cfr. INTRODUCTION.md, sez. "Codebase Patterns" + `contract.ts`):
 *  - Const tuple `as const` per i literal di dominio (`SEGNALAZIONE_REASONS`)
 *  - `z.enum(...)` su tuple per ottenere il check enum
 *  - `superRefine` per validazioni cross-field (ALTRO ⇒ detail required)
 *  - `z.infer<typeof schema>` per ottenere il tipo TypeScript
 *
 * NB: i DTO definiti qui (es. `operatorQueueItemSchema`) NON contengono PII
 * (`codiceFiscale`, `iban`, `datiStrutturati`). Sono la shape sicura che
 * attraversa il boundary HTTP verso la dashboard operatore — i campi PII
 * vengono recuperati solo nel detail-view dietro audit log esplicito.
 */
import { z } from 'zod';
/* -------------------------------------------------------------------------- */
/* Segnalazione errore (BLOCCATA)                                             */
/* -------------------------------------------------------------------------- */
/**
 * Categorie di segnalazione errore selezionabili dall'operatore al momento
 * di marcare una pratica come BLOCCATA. Mappate a etichette IT-IT in
 * `SEGNALAZIONE_REASON_LABELS`.
 */
export const SEGNALAZIONE_REASONS = [
    'DOCUMENTO_MANCANTE',
    'DOCUMENTO_ILLEGGIBILE',
    'DATI_ERRATI',
    'CONTRATTO_INCOMPLETO',
    'CODICE_FISCALE_ERRATO',
    'ALTRO',
];
/**
 * Etichette IT-IT per le categorie di segnalazione. Usate nella UI operatore
 * (checkbox label) e nell'email Brevo verso il cliente (Phase 3).
 */
export const SEGNALAZIONE_REASON_LABELS = {
    DOCUMENTO_MANCANTE: 'Documento mancante',
    DOCUMENTO_ILLEGGIBILE: 'Documento illeggibile',
    DATI_ERRATI: 'Dati errati',
    CONTRATTO_INCOMPLETO: 'Contratto incompleto',
    CODICE_FISCALE_ERRATO: 'Codice fiscale errato',
    ALTRO: 'Altro',
};
/**
 * Body della POST /api/v1/operator/pratiche/:id/segnala-errore.
 * Cross-field rule: se `reasons` include 'ALTRO', `detail` è obbligatorio
 * (non vuoto, max 500 caratteri).
 */
export const segnalazioneSchema = z
    .object({
    reasons: z
        .array(z.enum(SEGNALAZIONE_REASONS))
        .min(1, 'Seleziona almeno una categoria'),
    detail: z
        .string()
        .max(500, 'Massimo 500 caratteri')
        .nullable()
        .optional(),
})
    .superRefine((val, ctx) => {
    if (val.reasons.includes('ALTRO') && !val.detail?.trim()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['detail'],
            message: "Dettaglio obbligatorio per la categoria 'Altro'",
        });
    }
});
/* -------------------------------------------------------------------------- */
/* Queue filters                                                              */
/* -------------------------------------------------------------------------- */
/**
 * Query params della GET /api/v1/operator/queue. Pagination only in Phase 1
 * (filtri categorici verranno aggiunti in F-007.x).
 */
export const queueFiltersSchema = z
    .object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(50).default(20),
})
    .strict();
/* -------------------------------------------------------------------------- */
/* DTO shapes (no PII)                                                        */
/* -------------------------------------------------------------------------- */
/**
 * Item della coda operatore (lista). Volutamente NON espone:
 *  - codiceFiscale (PII)
 *  - ibanEncrypted (PII)
 *  - datiStrutturati (può contenere CF dei contraenti)
 *
 * Il PDF originale è raggiunto solo via endpoint presigned-URL dedicato che
 * audita ogni accesso (D-008 in INTRODUCTION.md).
 */
export const operatorQueueItemSchema = z.object({
    id: z.string(),
    stato: z.string(),
    tipologia: z.string(),
    submittedAt: z.string().nullable(),
    createdAt: z.string(),
    accountId: z.string(),
});
/**
 * Risposta paginata della GET /api/v1/operator/queue.
 */
export const operatorQueueResponseSchema = z.object({
    items: z.array(operatorQueueItemSchema),
    total: z.number().int().nonnegative(),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
});
/**
 * Detail-view di una pratica per l'operatore. A differenza dell'item lista
 * include `datiStrutturati` perché la dashboard di lavorazione lo richiede,
 * ma l'accesso è auditato (vedi `getDetail` in OperatorService).
 */
export const operatorPraticaDetailSchema = z.object({
    id: z.string(),
    accountId: z.string(),
    stato: z.string(),
    tipologia: z.string(),
    submittedAt: z.string().nullable(),
    createdAt: z.string(),
    assignedOperatorId: z.string().nullable(),
    assignedOperatorName: z.string().nullable(),
    assignedAt: z.string().nullable(),
    blockedAt: z.string().nullable(),
    blockedReasons: z.array(z.string()),
    blockedDetail: z.string().nullable(),
    pdfS3Key: z.string().nullable(),
    datiStrutturati: z.unknown(),
});
/* -------------------------------------------------------------------------- */
/* Action responses                                                           */
/* -------------------------------------------------------------------------- */
/**
 * Risposta della POST /api/v1/operator/pratiche/:id/take.
 */
export const takeResponseSchema = z.object({
    id: z.string(),
    stato: z.literal('IN_CARICO'),
    assignedOperatorId: z.string(),
    assignedOperatorName: z.string().nullable(),
    assignedAt: z.string(),
});
/**
 * Risposta della POST /api/v1/operator/pratiche/:id/release.
 * La pratica torna in `INVIATA` e gli assignment fields vengono ripuliti.
 */
export const releaseResponseSchema = z.object({
    id: z.string(),
    stato: z.literal('INVIATA'),
});
/**
 * Risposta della POST /api/v1/operator/pratiche/:id/segnala-errore.
 */
export const blockedResponseSchema = z.object({
    id: z.string(),
    stato: z.literal('BLOCCATA'),
    blockedAt: z.string(),
    blockedReasons: z.array(z.string()),
    blockedDetail: z.string().nullable(),
});
/**
 * Risposta della GET /api/v1/operator/pratiche/:id/pdf-url.
 * URL presigned con TTL 60s — ogni accesso è auditato.
 */
export const pdfUrlResponseSchema = z.object({
    url: z.string().url(),
});
//# sourceMappingURL=operator.js.map